/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { Box, Typography, Stack } from "@mui/material";
import { ArrowLeft, ArrowRight } from "@mui/icons-material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { getAPICall } from "lib/callApi";
import { GET_KPI_SELECTION_OPTIONS } from "lib/constants";
import {
  fetchDateRangeForWidget,
  formatDatesForWidget,
  handlePeriodsForWidget,
} from "utils/HelperFunctions";
import TooltipLight from "utils/TooltipLight";
import "../styles/widget.scss";
import Trend from "pages/Performance/components/Trend";
import CustomizeKpiViewModal from "./CustomizeKpiViewModal";

export default function IndividualKpi({ widget, updateWidgetMetadata }) {
  const { refresh: refreshKpiWidget, kpiActionId: refreshKpi } = useSelector(
    (state) => state.home.kpiWidgets
  );
  const { dataSource, widgetTitle, isCustomizable, kpiId, kpiName, title, timePeriod } = widget;
  const loginUser = useSelector((state) => state.loginUser.loginUserState);
  const [periodDates, setPeriodDates] = useState({});
  const [kpiData, setKpiData] = useState({ currentTotal: 0, previousTotal: 0 });
  const [widgetOptions, setWidgetOptions] = useState({
    kpiId,
    kpiName,
    title: title || kpiName || widgetTitle || "KPI View",
    timePeriod: timePeriod || "monthly",
  });
  const [selectionListOptions, setSelectionListOptions] = useState([]);
  const [open, setOpen] = useState(false);

  const handleCloseModal = () => setOpen(false);
  const handleOpenModal = async () => {
    const result = await getAPICall(GET_KPI_SELECTION_OPTIONS, true);
    if (result?.data?.success) {
      setSelectionListOptions(result.data.data);
      setOpen(true);
    }
  };
  const handleModalSave = (options) => {
    setWidgetOptions(options);
    setPeriodDates(fetchDateRangeForWidget(options.timePeriod, loginUser.fiscalYearStart));
    updateWidgetMetadata(widget.gridKey, options);
  };

  const handlePeriods = (type) => {
    if (!periodDates.start && !periodDates.end) return;
    const dates = handlePeriodsForWidget(type, widgetOptions.timePeriod, periodDates);
    if (
      dates.start &&
      dates.end &&
      dates.start !== periodDates.start &&
      dates.end !== periodDates.end
    ) {
      setPeriodDates(dates);
    }
  };

  const fetchKpiData = async () => {
    if (dataSource && periodDates.start && periodDates.end) {
      let payload = {
        start: periodDates.start,
        end: periodDates.end,
      };
      if (isCustomizable) {
        if (widgetOptions.kpiId === undefined) {
          return;
        }
        payload = { ...payload, kpiId: widgetOptions.kpiId };
      }
      const result = await getAPICall(dataSource, true, payload);
      if (result?.data?.success) {
        setKpiData(result.data.data);
      }
    }
  };

  useEffect(() => {
    if (!refreshKpiWidget) return;
    if (widgetOptions?.kpiId === refreshKpi) {
      fetchKpiData();
    }
  }, [refreshKpiWidget]);

  useEffect(() => {
    fetchKpiData();
  }, [periodDates, widgetOptions]);

  useEffect(() => {
    const dates = fetchDateRangeForWidget(widgetOptions.timePeriod, loginUser.fiscalYearStart);
    setPeriodDates(dates);
  }, []);

  return (
    <>
      <Stack
        p={2}
        sx={{
          overflowY: "auto",
          "::-webkit-scrollbar": {
            display: "none",
          },
          height: "100%",
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "3px",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography
              variant="h6"
              color="info"
              mb={0}
              // display="block"
              className="dragHandle"
              lineHeight="1rem"
            >
              {widgetOptions.title}
            </Typography>
            <Typography
              variant="h6"
              color="info"
              mb={0}
              sx={{
                fontSize: "0.75rem",
                color: "#888",
              }}
              lineHeight="1rem"
            >
              {widgetOptions.kpiName}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              pl: "0.15rem",
            }}
          >
            {isCustomizable && (
              <Box
                sx={{
                  width: "100%",
                  my: "-2px",
                }}
              >
                <TooltipLight title="Customize this widget" placement="left" variant="outline">
                  <MoreHorizIcon
                    style={{ cursor: "pointer", float: "right" }}
                    onClick={() => handleOpenModal()}
                  />
                </TooltipLight>
              </Box>
            )}

            {periodDates.start && (
              <Box
                sx={{
                  fontSize: "20px",
                  display: "flex",
                  alignItems: "center",
                  mx: "-10px",
                }}
              >
                <ArrowLeft
                  style={{ cursor: "pointer" }}
                  onClick={() => handlePeriods("prev")}
                  sx={{
                    mr: "-3px",
                  }}
                />
                <Typography variant="caption" color="info" mb={0} display="block" noWrap>
                  {formatDatesForWidget(
                    widgetOptions.timePeriod,
                    periodDates,
                    loginUser.fiscalYearStart
                  )}
                </Typography>
                <ArrowRight
                  style={{ cursor: "pointer" }}
                  onClick={() => handlePeriods("next")}
                  sx={{
                    ml: "-3px",
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            width: "100%",
            display: "inline-flex",
            justifyContent: "space-evenly",
            flexGrow: 1,
            alignItems: "center",
          }}
        >
          {widgetOptions.kpiName && (
            <Typography variant="h1" color="info" mb={0}>
              {kpiData.currentTotal}
              <Trend
                prev={kpiData.previousTotal}
                curr={kpiData.currentTotal}
                type="percent"
                displayTooltip={false}
              />
            </Typography>
          )}
        </Box>
      </Stack>
      <CustomizeKpiViewModal
        open={open}
        handleClose={handleCloseModal}
        selectionListOptions={selectionListOptions}
        handleSave={handleModalSave}
        widgetOptions={widgetOptions}
      />
    </>
  );
}
